import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getGoodsForInvoiceApi,
  generateIRNApi,
  getEInvoiceByIdApi,
  getDistanceApi,
  getAllEInvoicesApi,
  markAsTallyTransferredApi,
  cancelEInvoiceApi,
  getNextEInvoiceNumberApi,
  generateEPCInvoiceApi,
  GenerateEpcInvoiceRequest,
  cancelEPCInvoiceApi,
  getDispatchDetailsApi,
  getEinvoicesByCustomerIdApi,
} from "../../api/einvoice.api";
import ResponseModel, { PaginationRes } from "../../models/response.model";
import {
  IDispatchHistory,
  IOrderAddress,
  IOrderGoods,
} from "../../models/Order.model";
import { EInvoiceStatus, EwbDtls } from "../../models/invoice/invoice.model";

export interface DistanceResult {
  originPincode: string;
  destinationPincode: string;
  distanceText: string; // e.g., "1,245 km"
  distanceKm: number;
  distanceMeters: number; // e.g., 1245300
  durationText: string; // e.g., "22 hours 15 mins"
  durationSeconds: number; // e.g., 80100
  resolvedOrigin: string; // Full matched address from Google
  resolvedDestination: string;
}

// === STATE ===
interface EinvoiceState {
  loading: {
    generateIRN: boolean;
    getGoodsForInvoice: boolean;
    getInvoiceById: boolean;
    getDistance: boolean;
    getAllEInvoices: boolean;
    markAsTallyTransferred: boolean;
    cancelEInvoice: boolean;
    generateEPCInvoice: boolean;
    cancelEPCInvoice: boolean;
    getDispatchDetails: boolean;
    getEinvoicesByCustomerId: boolean;
  };
  goodsForInvoice: IOrderGoods[];
  distanceResult: DistanceResult | null;
  dispatchHistory?: IDispatchHistory | IDispatchHistory[];
  pagination: {
    getAllEInvoices?: PaginationRes;
  };
}

const initialState: EinvoiceState = {
  loading: {
    generateIRN: false,
    getGoodsForInvoice: false,
    getInvoiceById: false,
    getDistance: false,
    getAllEInvoices: false,
    markAsTallyTransferred: false,
    cancelEInvoice: false,
    generateEPCInvoice: false,
    cancelEPCInvoice: false,
    getDispatchDetails: false,
    getEinvoicesByCustomerId: false,
  },
  goodsForInvoice: [],
  distanceResult: null,
  pagination: {},
};
export const generateIRN = createAsyncThunk<
  ResponseModel,
  {
    invoiceNumber: string;
    orderId: string;
    EwbDtls?: EwbDtls;
    warehouseId?: string;
    billingAddress?: IOrderAddress;
    shippingAddress?: IOrderAddress;
    goodsIds: string[];
  },
  { rejectValue: string }
>(
  "orders/generateIRN",
  async (
    {
      invoiceNumber,
      orderId,
      EwbDtls,
      warehouseId,
      billingAddress,
      shippingAddress,
      goodsIds,
    },
    { rejectWithValue },
  ) => {
    try {
      return await generateIRNApi({
        documentNumber: invoiceNumber,
        orderId,
        EwbDtls,
        warehouseId,
        billingAddress,
        shippingAddress,
        goodsIds,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch history",
      );
    }
  },
);

export const getInvoiceById = createAsyncThunk<
  ResponseModel,
  {
    invoiceId: string;
  },
  { rejectValue: string }
>("orders/getInvoiceById", async ({ invoiceId }, { rejectWithValue }) => {
  try {
    return await getEInvoiceByIdApi({
      invoiceId,
    });
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch invoice",
    );
  }
});

export const getDistance = createAsyncThunk<
  ResponseModel,
  {
    originPin: string;
    destinationPin: string;
  },
  { rejectValue: string }
>(
  "orders/getDistance",
  async ({ originPin, destinationPin }, { rejectWithValue }) => {
    try {
      return await getDistanceApi({
        originPin,
        destinationPin,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch distance",
      );
    }
  },
);

export const getAllEInvoices = createAsyncThunk<
  ResponseModel,
  {
    query: {
      search?: string;
      page: number;
      limit: number;
      status: EInvoiceStatus;
    };
  },
  { rejectValue: string }
>("orders/getAllEInvoices", async ({ query }, { rejectWithValue }) => {
  try {
    return await getAllEInvoicesApi(query);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch all e-invoices",
    );
  }
});

export const markAsTallyTransferred = createAsyncThunk<
  ResponseModel,
  {
    invoiceId: string;
  },
  { rejectValue: string }
>(
  "orders/markAsTallyTransferred",
  async ({ invoiceId }, { rejectWithValue }) => {
    try {
      return await markAsTallyTransferredApi({
        invoiceId,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark as tally transferred",
      );
    }
  },
);

export const getGoodsForInvoice = createAsyncThunk<
  ResponseModel,
  {
    orderId: string;
  },
  { rejectValue: string }
>("orders/getGoodsForInvoice", async ({ orderId }, { rejectWithValue }) => {
  try {
    return await getGoodsForInvoiceApi({
      orderId,
    });
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch goods for invoice",
    );
  }
});
export const cancelEInvoice = createAsyncThunk<
  ResponseModel,
  {
    invoiceId: string;
    orderId: string;
  },
  { rejectValue: string }
>(
  "orders/cancelEInvoice",
  async ({ invoiceId, orderId }, { rejectWithValue }) => {
    try {
      return await cancelEInvoiceApi({
        invoiceId,
        orderId,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel e-invoice",
      );
    }
  },
);

export const getNextEInvoiceNumber = createAsyncThunk<
  ResponseModel,
  void,
  { rejectValue: string }
>("orders/getNextEInvoiceNumber", async (_, { rejectWithValue }) => {
  try {
    return await getNextEInvoiceNumberApi();
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to get next e-invoice number",
    );
  }
});

export const generateEPCInvoice = createAsyncThunk<
  ResponseModel,
  GenerateEpcInvoiceRequest,
  { rejectValue: string }
>("orders/generateEPCInvoice", async (data, { rejectWithValue }) => {
  try {
    return await generateEPCInvoiceApi(data);
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to generate EPC invoice",
    );
  }
});

export const cancelEPCInvoice = createAsyncThunk<
  ResponseModel,
  {
    invoiceId: string;
    paymentId: string;
  },
  { rejectValue: string }
>(
  "orders/cancelEPCInvoice",
  async ({ invoiceId, paymentId }, { rejectWithValue }) => {
    try {
      return await cancelEPCInvoiceApi({
        invoiceId,
        paymentId,
      });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel EPC invoice",
      );
    }
  },
);

export const getDispatchDetails = createAsyncThunk<
  ResponseModel,
  {
    orderId?: string;
    quotationId?: string;
    isEpc?: boolean;
  },
  { rejectValue: string }
>(
  "orders/getDispatchDetails",
  async ({ orderId, quotationId, isEpc }, { rejectWithValue }) => {
    try {
      return await getDispatchDetailsApi({ orderId, quotationId, isEpc });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get dispatch details",
      );
    }
  },
);

export const getEinvoicesByCustomerId = createAsyncThunk<
  ResponseModel,
  {
    customerId: string;
  },
  { rejectValue: string }
>(
  "orders/getEinvoicesByCustomerId",
  async ({ customerId }, { rejectWithValue }) => {
    try {
      return await getEinvoicesByCustomerIdApi({ customerId });
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to get e-invoices by customer ID",
      );
    }
  },
);

// === SLICE ===
const einvoiceSlice = createSlice({
  name: "einvoice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(generateIRN.pending, (state) => {
        state.loading.generateIRN = true;
      })
      .addCase(generateIRN.fulfilled, (state, action) => {
        state.loading.generateIRN = false;
        const payload = action.payload;
        console.log("IRN generated successfully:", payload);
      })
      .addCase(generateIRN.rejected, (state) => {
        state.loading.generateIRN = false;
      })
      .addCase(getGoodsForInvoice.pending, (state) => {
        state.loading.getGoodsForInvoice = true;
        state.goodsForInvoice = [];
      })
      .addCase(getGoodsForInvoice.fulfilled, (state, action) => {
        state.loading.getGoodsForInvoice = false;
        const goods = action.payload.data;
        state.goodsForInvoice = Array.isArray(goods)
          ? goods
          : Array.isArray(goods?.goods)
            ? goods.goods
            : [];
      })
      .addCase(getGoodsForInvoice.rejected, (state) => {
        state.loading.getGoodsForInvoice = false;
        state.goodsForInvoice = [];
      })
      .addCase(getInvoiceById.pending, (state) => {
        state.loading.getInvoiceById = true;
      })
      .addCase(getInvoiceById.fulfilled, (state, action) => {
        state.loading.getInvoiceById = false;
      })
      .addCase(getInvoiceById.rejected, (state) => {
        state.loading.getInvoiceById = false;
      })
      .addCase(getDistance.pending, (state) => {
        state.loading.getDistance = true;
      })
      .addCase(getDistance.fulfilled, (state, action) => {
        state.loading.getDistance = false;
        state.distanceResult = action.payload.data as DistanceResult;
      })
      .addCase(getDistance.rejected, (state) => {
        state.loading.getDistance = false;
      })
      .addCase(markAsTallyTransferred.pending, (state) => {
        state.loading.markAsTallyTransferred = true;
      })
      .addCase(markAsTallyTransferred.fulfilled, (state, action) => {
        state.loading.markAsTallyTransferred = false;
      })
      .addCase(markAsTallyTransferred.rejected, (state) => {
        state.loading.markAsTallyTransferred = false;
      })
      .addCase(cancelEInvoice.pending, (state) => {
        state.loading.cancelEInvoice = true;
      })
      .addCase(cancelEInvoice.fulfilled, (state, action) => {
        state.loading.cancelEInvoice = false;
      })
      .addCase(cancelEInvoice.rejected, (state) => {
        state.loading.cancelEInvoice = false;
      })
      .addCase(getAllEInvoices.pending, (state) => {
        state.loading.getAllEInvoices = true;
      })
      .addCase(getAllEInvoices.fulfilled, (state, action) => {
        state.loading.getAllEInvoices = false;
        state.pagination.getAllEInvoices = action.payload.pagination;
      })
      .addCase(getAllEInvoices.rejected, (state) => {
        state.loading.getAllEInvoices = false;
      })
      .addCase(generateEPCInvoice.pending, (state) => {
        state.loading.generateEPCInvoice = true;
      })
      .addCase(generateEPCInvoice.fulfilled, (state, action) => {
        state.loading.generateEPCInvoice = false;
      })
      .addCase(generateEPCInvoice.rejected, (state) => {
        state.loading.generateEPCInvoice = false;
      })
      .addCase(cancelEPCInvoice.pending, (state) => {
        state.loading.cancelEPCInvoice = true;
      })
      .addCase(cancelEPCInvoice.fulfilled, (state, action) => {
        state.loading.cancelEPCInvoice = false;
      })
      .addCase(cancelEPCInvoice.rejected, (state) => {
        state.loading.cancelEPCInvoice = false;
      })
      .addCase(getDispatchDetails.pending, (state) => {
        state.loading.getDispatchDetails = true;
        state.dispatchHistory = undefined;
      })
      .addCase(getDispatchDetails.fulfilled, (state, action) => {
        state.loading.getDispatchDetails = false;
        state.dispatchHistory = action.payload.data?.dispatchHistory;
      })
      .addCase(getDispatchDetails.rejected, (state) => {
        state.loading.getDispatchDetails = false;
        state.dispatchHistory = undefined;
      })
      .addCase(getEinvoicesByCustomerId.pending, (state) => {
        state.loading.getEinvoicesByCustomerId = true;
      })
      .addCase(getEinvoicesByCustomerId.fulfilled, (state, action) => {
        state.loading.getEinvoicesByCustomerId = false;
      })
      .addCase(getEinvoicesByCustomerId.rejected, (state) => {
        state.loading.getEinvoicesByCustomerId = false;
      });
  },
});

export default einvoiceSlice.reducer;
